import { Injectable }        from '@angular/core';
import { BehaviorSubject }   from 'rxjs';
import { AuthService }       from '../auth/auth.service';
import { AppEventType, SiteReviewService } from '../site-review.service';
import { FeatureFlagService } from '../../core/feature-flags/feature-flags.service';

interface IMenuItem {
  type:       string;
  name?:      string;
  state?:     string;
  icon?:      string;
  tooltip?:   string;
  disabled?:  boolean;
  sub?:       IChildItem[];
  featureCode?: string;
}

interface IChildItem {
  name:         string;
  state:        string;
  queryParams?: any;
  featureCode?: string;
}

@Injectable()
export class NavigationService {

  iconTypeMenuTitle: string = 'Frequently Accessed';

  private menuItems = new BehaviorSubject<IMenuItem[]>([]);
  menuItems$ = this.menuItems.asObservable();

  userData: any;
  filteredItems = [];

  constructor(
    private auth:    AuthService,
    private db:      SiteReviewService,
    private flags:   FeatureFlagService, 
  ) {
    this.getUserGroups();
    this.buildMenu();

    this.db.on(AppEventType.ApiUpdate).subscribe((t: any) => {
      if (t.entityName === 'Dashboard' || t.entityName === 'DashboardItem') {
        this.buildMenu();
      }
    });
  }

  // Rebuilds the menu so flag-gated items appear/disappear without a page reload.
  async rebuildMenu(): Promise<void> {
    await this.flags.refreshNavFlags();
    this.buildMenu();
  }

  // Filter a single menu item's sub-items by feature flags 

  private filterByFlags(items: IChildItem[]): IChildItem[] {
  return items.filter(item => {
    if (!item.featureCode) {
      return true;
    }

    return this.flags.isEnabled(item.featureCode);
  });
}

  hasDistrictAccess(districtId: number): boolean {
    const currentUser = this.auth.currentUser;
    if (!currentUser?.officeAccess || !Array.isArray(currentUser.officeAccess)) {
      return false;
    }

    return currentUser.officeAccess.some((office: any) => 
      office.hasAccess === 1 && 
      office.districtID === districtId
    );
  }

  getUserGroups() {
    const curUserData = this.auth.currentUser;

    if (!curUserData.groups) {
      console.log('groups not working!!');
      return;
    }

    this.userData = curUserData.groups;

    if (curUserData.userLevel.rank != 10) {
      this.defaultMenu = this.defaultMenu.filter(obj => obj.name != 'Administrative');
      this.defaultMenu = this.defaultMenu.filter(obj => obj.name != 'Development');
      this.defaultMenu = this.defaultMenu.filter(obj => obj.name != 'Crews');
    }

    this.filterMenu(curUserData.groups, 'fleet',   'Fleet');
    this.filterMenu(curUserData.groups, 'finance',  'Finance');
    this.filterMenu(curUserData.groups, 'fleet',   'Tooling');
    this.filterMenu(curUserData.groups, 'time',    'Time');
    this.filterMenu(curUserData.groups, 'penta',   'Penta');
  }

  filterMenu(groups: string[], groupName: string, menuName: string) {
    let devs = groups.filter(g => g.toLowerCase() === groupName.toLowerCase());
    if (devs.length == 0) {
      this.defaultMenu = this.defaultMenu.filter(obj => obj.name.toLowerCase() != menuName.toLowerCase());
    }
  }

  buildMenu() {
    const isDynamic = this.flags.isEnabled('DYNAMIC_MENU');
    const dashboardName = isDynamic ? 'Dynamic' : 'primary';

    //  data source based on DYNAMIC_MENU flag
    const dataSource$ = isDynamic
      ? this.db.getUserDashboardData()
      : this.db.getDashboardData();

    dataSource$.subscribe(res => {
      const hasDashboardNameField = res?.some(item => item.dashboardName !== undefined && item.dashboardName !== null);
      const dashboardScopedItems = hasDashboardNameField
        ? (res || []).filter(item => (item.dashboardName ?? '').trim().toLowerCase() === dashboardName.toLowerCase())
        : (res || []);

      // Exclude items marked 'Dynamic' or 'Deleted' status - applies to
      // whichever endpoint fed dataSource$ above.
      const items = dashboardScopedItems.filter(item =>
        item.status !== 'Dynamic' && item.status !== 'Deleted'
      );

      if (isDynamic) {
        this.buildDynamicMenu(items, dashboardName);
      } else {
        // Use existing menu building logic
        const flagFilteredMenu = this.defaultMenu
          .filter(section => this.isMenuEnabled(section))
          .map(section => ({
            ...section,
            sub: section.sub ? this.filterByFlags(section.sub) : []
          }))

        const originalMenu = this.defaultMenu;
        const canonicalOrder = originalMenu.map(m => m.name);
        this.defaultMenu = flagFilteredMenu;

        const outstandingMenuItems = this.appendMenuItemsToDefault(dashboardName, items);
        this.appendMenuItemsBeyondDefault(dashboardName, outstandingMenuItems);
        this.menuItems.next(this.sortCategories(this.defaultMenu, canonicalOrder));

        this.defaultMenu = originalMenu;
      }
    });
  }

  /**
   * If uri points at one of our own environment subdomains strips the scheme+host
   */
  private static readonly KNOWN_HOST_PATTERN =
    /^https?:\/\/(?:dev|beta|alpha|dash)\.pinnaclepowerservices\.com(?::\d+)?(\/[^\s]*)?$/i;

  private stripKnownDomain(uri: string): string | null {
    if (!uri) { return null; }
    const match = uri.match(NavigationService.KNOWN_HOST_PATTERN);
    if (!match) { return null; }
    const path = match[1] || '';
    return path.replace(/^\/+/, '');
  }

  private resolveItemState(uri: string, title: string, dashboardName: string): string {
    const stripped = this.stripKnownDomain(uri);
    if (stripped !== null) {
      return stripped;
    }
    if (uri && uri.indexOf('http') === 0) {
      return 'dashboard/link/' + dashboardName + '/' + title;
    }
    return uri;
  }

  private sortItemsBySortOrder<T extends { sortOrder?: number; title?: string; name?: string; [key: string]: any }>(items: T[]): T[] {
    const label = (item: T) => (item.title || item.name || '');
    return [...items].sort((a, b) => {
      const sortA = a.sortOrder || 0;
      const sortB = b.sortOrder || 0;
      if (sortA === 0 && sortB === 0) {
        return label(a).localeCompare(label(b));
      }
      if (sortA === 0) { return 1; }
      if (sortB === 0) { return -1; }
      return sortA - sortB;
    });
  }

  
  private sortCategories(sections: IMenuItem[], canonicalOrder: string[]): IMenuItem[] {
    const rankOf = (name: string) => {
      const idx = canonicalOrder.findIndex(n => n.toLowerCase() === (name || '').toLowerCase());
      return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
    };
    return [...sections].sort((a, b) => {
      const rankA = rankOf(a.name);
      const rankB = rankOf(b.name);
      if (rankA !== rankB) { return rankA - rankB; }
      return (a.name || '').localeCompare(b.name || '');
    });
  }

private isMenuEnabled(section: IMenuItem): boolean {
  if (!section.featureCode) {
    return true;
  }

  return this.flags.isEnabled(section.featureCode);
}

  // Build menu from SP data when DYNAMIC_MENU flag is enabled
  private buildDynamicMenu(spData: any[], dashboardName: string): void {
    if (!spData || !Array.isArray(spData)) {
      console.warn('[NavigationService] No SP data available for dynamic menu');
      this.menuItems.next([]);
      return;
    }

    // Group items by category
    const groupedItems = new Map<string, any[]>();
    spData.forEach(item => {
      const category = item.category || 'Uncategorized';
      if (!groupedItems.has(category)) {
        groupedItems.set(category, []);
      }
      groupedItems.get(category)!.push(item);
    });

    // Transform grouped items into IMenuItem structure
    const dynamicMenu: IMenuItem[] = Array.from(groupedItems.entries())
      .map(([category, items]) => {
        // Filter items by feature flag only (group access filter removed)
        const accessibleItems = this.sortItemsBySortOrder(
          items.filter(item => !item.featureCode || this.flags.isEnabled(item.featureCode))
        );

        // Transform to IChildItem structure
        const subItems: IChildItem[] = accessibleItems.map(item => ({
          name: item.title || item.name || 'Untitled',
          state: this.resolveItemState(item.uri || '', item.title, dashboardName),
          queryParams: item.queryParams,
          featureCode: item.featureCode
        }));

        // Map category to icon (similar to existing defaultMenu)
        const icon = this.getIconForCategory(category);

        return {
          type: 'dropDown',
          name: category,
          tooltip: category,
          icon: icon,
          sub: subItems
        };
      });

  
    const canonicalOrder = this.defaultMenu.map(m => m.name);
    this.menuItems.next(this.sortCategories(dynamicMenu, canonicalOrder));
  }

  private getIconForCategory(category: string): string {
    const iconMap: Record<string, string> = {
      'Operations': 'assignment',
      'Time': 'business',
      'Civil': 'engineering',
      'Fleet': 'fence',
      'Finance': 'business',
      'Administrative': 'security',
      'Crews': 'supervisor_account',
      'Safety': 'security',
      'Dashboards': 'dashboard',
      'Development': 'engineering',
      'Tooling': 'fence',
      'Penta': 'security'
    };

    return iconMap[category] || 'link';
  }

  appendMenuItemsToDefault(dashboardName, customMenuItems) {
    let outstandingMenuItems = [];

    customMenuItems.forEach(item => { item.added = false; });

    this.defaultMenu.forEach(m => {
      let items = this.sortItemsBySortOrder(customMenuItems.filter(res => res.category === m.name));
      let subs = [];
      items.forEach(item => {
        let state = this.resolveItemState(item.uri, item.title, dashboardName);

        item.added = true;
        subs.push({ name: item.title, state });
      });
      m.sub.push.apply(m.sub, subs);
    });

    outstandingMenuItems = customMenuItems.filter(res => !res.added);
    this.filteredItems = outstandingMenuItems;

    return outstandingMenuItems;
  }

  appendMenuItemsBeyondDefault(dashboardName, outstandingMenuItems) {
    var categories = [];
    outstandingMenuItems.forEach(mi => {
      let exists = categories.filter(cats => cats === mi.category);
      if (exists.length === 0) categories.push(mi.category);
    });

    categories.forEach(cat => {
      let menuItem: IMenuItem = {
        type: 'dropDown', name: cat, tooltip: 'tool tip here', icon: 'link', sub: []
      };

      let items = this.sortItemsBySortOrder(outstandingMenuItems.filter(res => res.category === cat));
      items.forEach(item => {
        let state = this.resolveItemState(item.uri, item.title, dashboardName);

        menuItem.sub.push({ name: item.title, state });
      });

      this.defaultMenu.push(menuItem);
    });
  }

  // Items without featureCode are always visible.

  defaultMenu: IMenuItem[] = [
    {
      name: 'Operations',
      type: 'dropDown',
      tooltip: 'Operations',
      icon: 'assignment',
      sub: [
        { name: 'Active Jobs',              state: 'reviews/jobs' },

        { 
          name: 'Active Work Orders', 
          state: 'reviews/socal', 
          featureCode: 'WO_DISTRICT_2' 
        },


        { name: 'Archived Jobs',            state: 'reviews/archived-jobs' },
        { name: 'NTP/WA Tracker',              state: 'reviews/pc-tracker' },
        { name: 'Cost/Forecast Est.',       state: 'finance/cost-estimator' },
        //{ name: 'Subcontractor Invoices',   state: 'reviews/subcontractor-reports' },
        { name: 'Customers',                state: 'customers' },
        { name: 'Imports',                  state: 'reviews/job-imports' },
        { name: 'Equipment List',           state: 'reviews/equipment-list-operations' },
        { name: 'Weekly Billing',           state: 'reviews/weekly-billing' },


        //  example of how to enable WEEKLY_BILLING_PIVOT in those envs via the Flags admin UI
        { name: 'Weekly Billing Pivot',     state: 'reviews/weekly-billing-pivot',  featureCode: 'WB_PIVOT' },
        //{ name: 'Weekly Billing Pivot',     state: 'reviews/weekly-billing-pivot'},
        { name: 'Weekly Billing Invoice Status Pivot',     state: 'reviews/weekly-billing-invoice-status', featureCode: 'M_WB_INV_STATUS'},
        { name: 'Weekly Billing Underbilled',     state: 'reviews/weekly-billing-underbilled', featureCode: 'M_WB_UNDERB'},

        { name: 'PG&E Reporting',     state: 'reviews/pge-reporting', featureCode: 'M_PGE_R'},
        { name: 'Workbook Upload',          state: 'reviews/workbook-upload' },
        { name: 'QA Module',                state: 'reviews/qa' },
        
        { name: 'Timesheet Forms',          state: 'reviews/timesheet-forms', featureCode: 'TS_FORMS_TMPL' },

        { name: 'Tag Log',                  state: 'reviews/tags', featureCode: 'TAG_LOG_GRID' },
        { name: 'Expense Reimbursement',    state: 'reviews/expense-reimbursement', featureCode: 'EXPENSE_REIM' },

      ]
    },
    {
      name: 'Time',
      type: 'dropDown',
      tooltip: 'TimeSheet',
      icon: 'business',
      sub: [
        { name: 'Time Approval',  state: 'reviews/timesheets-approval' },
        { name: 'PA Review',    state: 'reviews/timesheets-pa-review' },
      ]
    },
    {
      name: 'Civil',
      type: 'dropDown',
      tooltip: 'Civil Operations',
      icon: 'engineering',
      sub: [
        { name: 'Civil Work Needed', state: 'reviews/civil-work-needed' },
        { name: 'USA Tickets',       state: 'reviews/usa-tickets' },
      ]
    },
    {
      name: 'Fleet',
      type: 'dropDown',
      tooltip: 'Fleet',
      icon: 'fence',
      sub: [
        { name: 'Equipment List',  state: 'fleet/vehicles' },
        { name: 'Repair Orders',   state: 'fleet/repair-orders' },
        { name: 'Equipment Types', state: 'fleet/equipment-types' },
        { name: 'PM List',         state: 'fleet/pm-list' },
      ]
    },
    {
      name: 'Finance',
      type: 'dropDown',
      tooltip: 'Finance',
      icon: 'business',
      sub: [
        { name: 'Finance Review and Export', state: 'reviews/financial-ts-review' },
        { name: 'Batch Exports',             state: 'reviews/timesheets/batch-exports' },
      ]
    },
    {
      name: 'Tooling',
      type: 'dropDown',
      tooltip: 'Fleet',
      icon: 'fence',
      sub: []
    },
    {
      name: 'Penta',
      type: 'dropDown',
      tooltip: 'Administration',
      icon: 'security',
      sub: [
        { name: 'Penta', state: 'reviews/dynagrid' },
        { name: 'Timesheet Lookup', state: 'reviews/penta-timesheet-lookup', featureCode: 'PT_LOOKUP' },
      ]
    },
    {
      name: 'Administrative',
      type: 'dropDown',
      tooltip: 'Administration',
      icon: 'security',
      sub: [
        { name: 'Users',                    state: 'admin/users' },
        { name: 'Groups',                   state: 'admin/teams' },
        { name: 'Offices',                  state: 'admin/offices' },
        { name: 'Calendars',                state: 'admin/calendars' },
        { name: 'Timesheets',               state: 'reviews/timesheets' },
        { name: 'Timesheets Graph Report',  state: 'reviews/timesheets-graph-report' },
        { name: 'Timesheets Admin Report',  state: 'reviews/timesheets-admin-report' },
        { name: 'Dashboards',              state: 'admin/dashboards' },
        { name: 'Job Types',               state: 'admin/job-types' },
        { name: 'Unit Types',               state: 'admin/unit-types' },
        { name: 'Cost Types',               state: 'admin/cost-types' },
        { name: 'Pay Types',               state: 'admin/pay-types' },
        { name: 'Alerts',                  state: 'admin/alerts' },
        { name: 'Release Notes',           state: 'admin/release-notes', featureCode: 'REL_NOTES' },
        { name: 'Report Templates',        state: 'reviews/templates' },
        { name: 'Project Based Work',      state: 'admin/project-based-work' },
        { name: 'Template Manager',        state: 'admin/template-manager' },
        { name: 'Folder Templates',        state: 'admin/folder-templates', featureCode: 'FLDR_MGR' },
        { name: 'Settings',               state: 'admin/settings' },
        { name: 'Feature Flags',          state: 'admin/feature-flags' },
        { name: 'Phase Manager',          state: 'admin/phase-manager', featureCode: 'PHASE_MGR' },
        { name: 'AI Prompts',            state: 'admin/ai-prompts', featureCode: 'AI_PROMPTS_ADM' },
        { name: 'Timesheet Forms Templates', state: 'admin/timesheet-forms-templates', featureCode: 'TS_FORMS_TMPL' },
      ]
    },
    {
      name: 'Crews',
      type: 'dropDown',
      tooltip: 'New Items',
      icon: 'supervisor_account',
      sub: [
        { name: 'Crew Locator', state: 'crews/crews' },
        { name: 'Crew Regions', state: 'reviews/maps' },
        { name: 'Members (dev)',state: 'crews/crew-member-manager' },
        { name: 'Yards',        state: 'crews/yards' },
      ]
    },
    {
      featureCode: 'M_SAFETY',
      name: 'Safety',
      type: 'dropDown',
      tooltip: 'New Items',
      icon: 'security',
      sub: [
        { name: 'Incident Log', state: 'reviews/incidents' },
      ]
    },
    {
      featureCode: 'M_DASHBOARDS',
      name: 'Dashboards',
      type: 'dropDown',
      tooltip: 'New Items',
      icon: 'dashboard',
      sub: [
      ]
    },
    {
      name: 'Development',
      type: 'dropDown',
      tooltip: 'New Items',
      icon: 'engineering',
      sub: [
        { name: 'Dashboard',   state: 'dashboard' },
        { name: 'Job Reports', state: 'reviews/jobreports' },
        { name: 'AI PDF Test', state: 'reviews/jobs/ai-test', featureCode: 'AI_PDF_TEST' },
      ]
    },
  ];
}